"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  COMPOUNDING_KEYS,
  CurrencyField,
  frequencyOptions,
  perYearOf,
  useMoney,
} from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import {
  cashFlowSchedule,
  type DiscountRow,
  yearlyFactors,
} from "@/lib/tool-kit/finance/discounting";
import {
  CONTINUOUS,
  growthFactor,
  presentValueAnnuity,
  presentValuePerpetuity,
  type Timing,
} from "@/lib/tool-kit/finance/tvm";

/** English strings. A translated content file overrides these through `ui`. */
export const PRESENT_VALUE_CALCULATOR_STRINGS = {
  inputsHeading: "Your numbers",
  kind: "What are you discounting?",
  kindLump: "A single future amount",
  kindAnnuity: "A series of equal payments",
  kindPerpetuity: "A payment that never ends",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  futureAmount: "Future amount",
  payment: "Payment each period",
  rate: "Annual discount rate (%)",
  perYear: "Periods per year",
  years: "Time (years)",
  growth: "Payment growth per period (%)",
  timing: "Payments are made at the",
  timingEnd: "End of each period (ordinary annuity)",
  timingBegin: "Start of each period (annuity due)",
  annually: "Annually (1 per year)",
  semiannually: "Semiannually (2 per year)",
  quarterly: "Quarterly (4 per year)",
  bimonthly: "Every two months (6 per year)",
  monthly: "Monthly (12 per year)",
  semimonthly: "Twice a month (24 per year)",
  biweekly: "Every two weeks (26 per year)",
  weekly: "Weekly (52 per year)",
  daily: "Daily (365 per year)",
  continuous: "Continuously",
  resultsHeading: "Results",
  presentValue: "Present value",
  totalReceived: "Total of the payments",
  discountAmount: "Value lost to discounting",
  periodRate: "Rate per period",
  periodsCount: "Number of periods",
  endFactor: "Discount factor at the end",
  workingHeading: "The working",
  workLump:
    "PV = FV ÷ (1 + {rate} ÷ {m})^({m} × {years}) = {fv} ÷ {growth} = {pv}",
  workLumpContinuous:
    "PV = FV × e^(−{rate} × {years}) = {fv} × {factor} = {pv}",
  workAnnuity:
    "PV = PMT × [1 − (1 + i)^−n] ÷ i = {pmt} × [1 − (1 + {i})^−{n}] ÷ {i} = {pv}",
  workAnnuityDue: "Annuity due: the ordinary-annuity value × (1 + {i}) = {pv}",
  workPerpetuity: "PV = PMT ÷ i = {pmt} ÷ {i} = {pv}",
  workGrowingPerpetuity: "PV = PMT ÷ (i − g) = {pmt} ÷ ({i} − {g}) = {pv}",
  workPerpetuityDue: "Payments at the start of each period: × (1 + {i}) = {pv}",
  tableHeading: "Discount factor table",
  yearColumn: "Year",
  periodColumn: "Period",
  factorColumn: "Discount factor",
  presentColumn: "Present value",
  amountColumn: "Payment",
  runningColumn: "Running total",
  lumpTableNote:
    "The present value of the future amount if it were due at the end of each year.",
  tableLimit: "Showing the first {shown} of {total} periods.",
  perpetuityNote:
    "A perpetuity has no last period. These are the first {shown} payments; the running total approaches the present value above.",
  invalidRate: "Enter a discount rate of 0 or more.",
  invalidAmount: "Enter an amount of 0 or more.",
  invalidTime: "Enter a time between 0 and 100 years.",
  rateBelowGrowth:
    "A growing perpetuity needs a discount rate per period above the growth rate; otherwise its value is not finite.",
  convention:
    "The rate per period is the annual rate ÷ periods per year, and payments occur once per period.",
} as const;

type Strings = typeof PRESENT_VALUE_CALCULATOR_STRINGS;
type Kind = "lump" | "annuity" | "perpetuity";

const MAX_YEARS = 100;
const TABLE_LIMIT = 60;
const PERPETUITY_ROWS = 12;

interface Form {
  kind: Kind;
  amount: string;
  rate: string;
  perYear: string;
  years: string;
  growth: string;
  timing: Timing;
}

interface Pv {
  pv: number;
  total: number | null;
  /** Rate per period (annual rate for continuous). */
  i: number;
  periods: number | null;
  endFactor: number | null;
  rows: DiscountRow[];
  years: { year: number; factor: number }[];
  tableTotal: number;
}

function periodsFor(years: number, perYear: number): number {
  return Math.max(1, Math.round(years * perYear));
}

function lumpPv(form: Form, rate: number, years: number, amount: number): Pv {
  const perYear = perYearOf(form.perYear);
  const factor = 1 / growthFactor(rate, perYear, years);
  return {
    pv: amount * factor,
    total: amount,
    i: perYear === CONTINUOUS ? rate : rate / perYear,
    periods: perYear === CONTINUOUS ? null : periodsFor(years, perYear),
    endFactor: factor,
    rows: [],
    years: yearlyFactors(rate, perYear, years),
    tableTotal: 0,
  };
}

function annuityPv(
  form: Form,
  rate: number,
  years: number,
  amount: number
): Pv {
  const perYear = perYearOf(form.perYear);
  const i = rate / perYear;
  const n = periodsFor(years, perYear);
  const rows = cashFlowSchedule({
    payment: amount,
    growth: 0,
    i,
    count: Math.min(n, TABLE_LIMIT),
    timing: form.timing,
  });
  return {
    pv: presentValueAnnuity(amount, i, n, form.timing),
    total: amount * n,
    i,
    periods: n,
    endFactor: (1 + i) ** -(form.timing === "begin" ? n - 1 : n),
    rows,
    years: [],
    tableTotal: n,
  };
}

function perpetuityPv(
  form: Form,
  rate: number,
  amount: number,
  g: number
): Pv | null {
  const i = rate / perYearOf(form.perYear);
  const pv = presentValuePerpetuity(amount, i, g, form.timing);
  if (pv === null) {
    return null;
  }
  return {
    pv,
    total: null,
    i,
    periods: null,
    endFactor: null,
    rows: cashFlowSchedule({
      payment: amount,
      growth: g,
      i,
      count: PERPETUITY_ROWS,
      timing: form.timing,
    }),
    years: [],
    tableTotal: 0,
  };
}

interface Outcome {
  pv: Pv | null;
  error: string | null;
}

function compute(form: Form, strings: Strings): Outcome {
  const rate = parseNumberInput(form.rate);
  const amount = parseNumberOr(form.amount, 0);
  const years = parseNumberInput(form.years);
  if (rate === null || rate < 0) {
    return { pv: null, error: strings.invalidRate };
  }
  if (amount < 0) {
    return { pv: null, error: strings.invalidAmount };
  }
  if (form.kind === "perpetuity") {
    const g = parseNumberOr(form.growth, 0) / 100;
    const pv = perpetuityPv(form, rate / 100, amount, g);
    return pv
      ? { pv, error: null }
      : { pv: null, error: strings.rateBelowGrowth };
  }
  if (years === null || years <= 0 || years > MAX_YEARS) {
    return { pv: null, error: strings.invalidTime };
  }
  const build = form.kind === "lump" ? lumpPv : annuityPv;
  return { pv: build(form, rate / 100, years, amount), error: null };
}

function workingLines(
  form: Form,
  pv: Pv,
  strings: Strings,
  fmt: Format
): string[] {
  const money = fmt.money;
  const num = (v: number) => fmt.num(v, 8);
  const values = {
    rate: num(Number(parseNumberOr(form.rate, 0)) / 100),
    m: fmt.num(perYearOf(form.perYear), 4),
    years: num(parseNumberOr(form.years, 0)),
    fv: money(parseNumberOr(form.amount, 0)),
    pmt: money(parseNumberOr(form.amount, 0)),
    growth: num(pv.endFactor ? 1 / pv.endFactor : 1),
    factor: num(pv.endFactor ?? 1),
    pv: money(pv.pv),
    i: num(pv.i),
    n: fmt.num(pv.periods ?? 0, 4),
    g: num(parseNumberOr(form.growth, 0) / 100),
  };
  if (form.kind === "lump") {
    const template =
      perYearOf(form.perYear) === CONTINUOUS
        ? strings.workLumpContinuous
        : strings.workLump;
    return [t(template, values)];
  }
  const due = form.timing === "begin";
  if (form.kind === "annuity") {
    return [
      t(strings.workAnnuity, {
        ...values,
        pv: money(pv.pv / (due ? 1 + pv.i : 1)),
      }),
      ...(due ? [t(strings.workAnnuityDue, values)] : []),
    ];
  }
  const grows = parseNumberOr(form.growth, 0) !== 0;
  return [
    t(grows ? strings.workGrowingPerpetuity : strings.workPerpetuity, {
      ...values,
      pv: money(pv.pv / (due ? 1 + pv.i : 1)),
    }),
    ...(due ? [t(strings.workPerpetuityDue, values)] : []),
  ];
}

interface Format {
  money: (v: number) => string;
  num: (v: number, digits?: number) => string;
}

function Table({
  form,
  pv,
  strings,
  fmt,
}: {
  form: Form;
  pv: Pv;
  strings: Strings;
  fmt: Format;
}) {
  const factor = (v: number) => fmt.num(v, 6);
  if (form.kind === "lump") {
    const amount = parseNumberOr(form.amount, 0);
    return (
      <>
        <p className={HINT}>{strings.lumpTableNote}</p>
        <DataTable
          head={[
            strings.yearColumn,
            strings.factorColumn,
            strings.presentColumn,
          ]}
          rows={pv.years.map((row) => [
            fmt.num(row.year, 4),
            factor(row.factor),
            fmt.money(amount * row.factor),
          ])}
        />
      </>
    );
  }
  return (
    <>
      <DataTable
        head={[
          strings.periodColumn,
          strings.factorColumn,
          strings.amountColumn,
          strings.presentColumn,
          strings.runningColumn,
        ]}
        rows={pv.rows.map((row) => [
          fmt.num(row.period, 0),
          factor(row.factor),
          fmt.money(row.cashFlow),
          fmt.money(row.present),
          fmt.money(row.running),
        ])}
      />
      {form.kind === "perpetuity" ? (
        <p className={HINT}>
          {t(strings.perpetuityNote, { shown: fmt.num(pv.rows.length, 0) })}
        </p>
      ) : null}
      {form.kind === "annuity" && pv.tableTotal > pv.rows.length ? (
        <p className={HINT}>
          {t(strings.tableLimit, {
            shown: fmt.num(pv.rows.length, 0),
            total: fmt.num(pv.tableTotal, 0),
          })}
        </p>
      ) : null}
    </>
  );
}

function Results({
  form,
  pv,
  currency,
  strings,
}: {
  form: Form;
  pv: Pv;
  currency: string;
  strings: Strings;
}) {
  const numFmt = useToolNumber();
  const money = useMoney(currency);
  const fmt: Format = {
    money,
    num: (v, digits = 6) => numFmt(v, { maximumFractionDigits: digits }),
  };
  const lines = workingLines(form, pv, strings, fmt);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.presentValue} value={money(pv.pv)} />
        {pv.total === null ? null : (
          <StatCard label={strings.totalReceived} value={money(pv.total)} />
        )}
        {pv.total === null ? null : (
          <StatCard
            label={strings.discountAmount}
            value={money(pv.total - pv.pv)}
          />
        )}
        <StatCard
          label={strings.periodRate}
          value={`${numFmt(pv.i * 100, { maximumFractionDigits: 6 })}%`}
        />
        {pv.periods === null ? null : (
          <StatCard label={strings.periodsCount} value={numFmt(pv.periods)} />
        )}
        {pv.endFactor === null ? null : (
          <StatCard
            label={strings.endFactor}
            value={fmt.num(pv.endFactor, 6)}
          />
        )}
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        {lines.map((line) => (
          <p
            className="font-mono text-muted-foreground text-sm leading-relaxed"
            key={line}
          >
            {line}
          </p>
        ))}
      </div>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <Table fmt={fmt} form={form} pv={pv} strings={strings} />
    </section>
  );
}

export default function PresentValueCalculatorWidget() {
  const strings = useToolUi(PRESENT_VALUE_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    kind: "annuity",
    amount: "500",
    rate: "8",
    perYear: "12",
    years: "20",
    growth: "0",
    timing: "end",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const { kind } = form;
  // Continuous discounting only has a meaning for a single amount.
  const perYear =
    kind !== "lump" && form.perYear === "continuous" ? "1" : form.perYear;
  const effective = { ...form, perYear };
  const outcome = compute(effective, strings);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <PillGroup
          label={strings.kind}
          name="pv-kind"
          onChange={(value) => update({ kind: value })}
          options={[
            { value: "lump", label: strings.kindLump },
            { value: "annuity", label: strings.kindAnnuity },
            { value: "perpetuity", label: strings.kindPerpetuity },
          ]}
          value={kind}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="pv-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="pv-amount"
            label={kind === "lump" ? strings.futureAmount : strings.payment}
            numeric
            onChange={(value) => update({ amount: value })}
            value={form.amount}
          />
          <TextField
            id="pv-rate"
            label={strings.rate}
            numeric
            onChange={(value) => update({ rate: value })}
            value={form.rate}
          />
          <SelectField
            id="pv-per-year"
            label={strings.perYear}
            onChange={(value) => update({ perYear: value })}
            options={frequencyOptions(
              strings,
              COMPOUNDING_KEYS,
              kind === "lump"
            )}
            value={perYear}
          />
          {kind === "perpetuity" ? (
            <TextField
              id="pv-growth"
              label={strings.growth}
              numeric
              onChange={(value) => update({ growth: value })}
              value={form.growth}
            />
          ) : (
            <TextField
              id="pv-years"
              label={strings.years}
              numeric
              onChange={(value) => update({ years: value })}
              value={form.years}
            />
          )}
        </div>
        {kind === "lump" ? null : (
          <PillGroup
            label={strings.timing}
            name="pv-timing"
            onChange={(value) => update({ timing: value })}
            options={[
              { value: "end", label: strings.timingEnd },
              { value: "begin", label: strings.timingBegin },
            ]}
            value={form.timing}
          />
        )}
        <p className={HINT}>{strings.convention}</p>
      </section>
      {outcome.pv ? (
        <Results
          currency={currency}
          form={effective}
          pv={outcome.pv}
          strings={strings}
        />
      ) : (
        <Notice>{outcome.error}</Notice>
      )}
    </div>
  );
}
